import type { Job } from "@/data/mockJobs";

interface SwipeCardProps {
  job: Job;
  onSwipe?: (direction: "left" | "right") => void;
  onNope?: () => void;
  onYes?: () => void;
  style?: React.CSSProperties;
  isDragging?: boolean;
}

export function SwipeCard({ job, onNope, onYes, style, isDragging }: SwipeCardProps) {
  return (
    <div
      className={`h-full w-full select-none ${isDragging ? "cursor-grabbing" : "cursor-grab"}`}
      style={style}
    >
      <div className="flex h-full flex-col overflow-hidden rounded-3xl bg-card-white shadow-depth-3">
        {/* Header with red background and code icon */}
        <div className="relative bg-primary-accent px-5 py-5">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              {/* Code icon */}
              <div className="mt-1 flex-shrink-0">
                <svg
                  className="h-8 w-8 text-white"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="16 18 22 12 16 6"></polyline>
                  <polyline points="8 6 2 12 8 18"></polyline>
                </svg>
              </div>
              <h2 className="text-2xl font-bold leading-tight text-white">{job.title}</h2>
            </div>
            <span className="mt-1 flex flex-shrink-0 items-center gap-1 text-sm text-white/90">
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                  clipRule="evenodd"
                />
              </svg>
              {job.location}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 space-y-4 overflow-hidden bg-white px-5 py-4">
          {/* Company with lock icon (anonymous) */}
          <div className="flex items-center gap-2">
            <svg className="h-5 w-5 text-gray-500" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 1C8.676 1 6 3.676 6 7v2H4v14h16V9h-2V7c0-3.324-2.676-6-6-6zm0 2c2.276 0 4 1.724 4 4v2H8V7c0-2.276 1.724-4 4-4z" />
            </svg>
            <span className="text-lg font-semibold text-gray-900">{job.company}</span>
          </div>

          {/* Salary with coin icon - only show if provided */}
          {job.salary && (
            <div className="flex items-center gap-2">
              <span className="text-lg text-yellow-500">●</span>
              <span className="text-lg text-gray-900">{job.salary}</span>
            </div>
          )}

          {/* Employment type with checkmark */}
          <div className="flex items-center gap-2">
            <svg className="h-5 w-5 text-primary-accent" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
            <span className="text-gray-900">{job.type}</span>
          </div>

          {/* Requirements - no header, just list with checkmarks */}
          <div className="space-y-2">
            {job.requirements.slice(0, 3).map((req, i) => (
              <div key={i} className="flex items-start gap-2">
                <svg
                  className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary-accent"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-gray-900">{req}</span>
              </div>
            ))}
          </div>

          {/* Tags */}
          <div className="flex flex-wrap gap-2">
            {job.tags.slice(0, 4).map((tag, i) => (
              <span
                key={i}
                className="rounded-full border border-gray-200 bg-gray-100 px-4 py-1.5 text-sm font-medium text-gray-700"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Nope / Yes buttons at bottom */}
        <div className="flex-shrink-0 bg-white px-5 pb-5 pt-2">
          <div className="flex gap-3">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onNope?.();
              }}
              className="flex-1 rounded-full bg-[#E8E4DC] py-3.5 text-lg font-semibold text-gray-800 transition-all hover:bg-[#DDD8D0] active:scale-95"
            >
              Nope
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onYes?.();
              }}
              className="flex-1 rounded-full bg-[#4ADE80] py-3.5 text-lg font-semibold text-white transition-all hover:bg-[#3FCF70] active:scale-95"
            >
              Liker
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
